/**
 * Detecting a Caltrans camera that is down.
 *
 * Caltrans reports an outage *in band*: it serves a "Temporarily Unavailable"
 * graphic with HTTP 200, correct dimensions and a normal-looking JPEG. Nothing
 * about the response says anything is wrong, so `onerror` never fires and the
 * app happily displays a picture of the words "Temporarily Unavailable".
 *
 * The placeholder is a white card with dark text, and it is byte-stable apart
 * from the burnt-in timestamp. Measured across the eight cameras, the separation
 * is wide:
 *
 *   offline (4 cameras):  white 91.6%   saturation 7.5   colours 46
 *   online  (4 cameras):  white 0.3-4.3%  saturation 13-36  colours 39-97
 *
 * Caltrans sends `Access-Control-Allow-Origin: *`, so the pixels can be read
 * without tainting the canvas.
 */

export interface FrameStats {
  /** Percentage of near-white pixels in the picture area. */
  whitePct: number;
  /** Mean chroma; a photo has colour, a white card does not. */
  meanSat: number;
  /** Distinct coarse colour buckets. */
  colors: number;
}

/** Ignore the bottom strip, which is the burnt-in timestamp bar on every frame. */
const PICTURE_FRACTION = 0.8;

/**
 * Thresholds sit far from both populations rather than splitting the difference:
 * the whitest real frame measured 4.3%.
 *
 * Saturation and colour count are required as well because dense fog — which
 * this coast produces constantly — is the one real scene that can be mostly
 * bright. Fog still carries gradient and noise; the placeholder is flat.
 */
export function looksOffline(s: FrameStats): boolean {
  return s.whitePct >= 70 && s.meanSat <= 12 && s.colors <= 70;
}

export function analyzeFrame(data: Uint8ClampedArray, pixelCount: number): FrameStats {
  let white = 0;
  let sat = 0;
  const buckets = new Set<number>();

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    if (r > 235 && g > 235 && b > 235) white++;
    sat += Math.max(r, g, b) - Math.min(r, g, b);
    buckets.add(((r >> 5) << 10) | ((g >> 5) << 5) | (b >> 5));
  }

  return {
    whitePct: (100 * white) / pixelCount,
    meanSat: sat / pixelCount,
    colors: buckets.size,
  };
}

/** How much of the frame height carries picture rather than timestamp. */
export function pictureHeight(height: number): number {
  return Math.max(1, Math.round(height * PICTURE_FRACTION));
}
